import DeltaDownIcon from '@assets/icons/delta-down.svg';
import DeltaUpIcon from '@assets/icons/delta-up.svg';
import {
  ArrowDirection,
  ColumnId,
  PricingGroupedTableRow,
  PricingItemManagementTableRow,
} from '@sales-portal/domain/pricing/screens/management/types';
import { FilterFn, Row } from '@tanstack/react-table';
import { Theme } from '@/contexts/theme-context';
import { VariantPackCostChange } from '@sales-portal/domain/pricing/screens/management/blocs/transformers/use-pricing-management-transformers';

// Typed as Set<string> for flexibility across different pricing tables/screens
const LEFT_ALIGNED_COLUMNS = new Set<string>([
  ColumnId.Checkbox,
  ColumnId.Name,
  ColumnId.Id,
  ColumnId.PeerRange,
  ColumnId.UOM,
  ColumnId.Category,
]);

const DELTA_COLUMNS = new Set<string>([
  ColumnId.CostDelta,
  ColumnId.GrossMarginPercentageDelta,
  ColumnId.PriceDelta,
  ColumnId.WeeklyCostChangeGrossProfitImpact,
  ColumnId.WeeklyEditsGrossProfitImpact,
  ColumnId.WeeklyNetGrossProfitImpact,
]);

const INPUT_COLUMNS = new Set<string>([
  ColumnId.CurrentGrossMargin,
  ColumnId.CurrentPrice,
]);

export const isLeftAlignedColumn = (columnId: string) => {
  return LEFT_ALIGNED_COLUMNS.has(columnId);
};

export const isDeltaColumn = (columnId: string) => {
  return DELTA_COLUMNS.has(columnId);
};

export const isInputColumn = (columnId: string) => {
  return INPUT_COLUMNS.has(columnId);
};

export const getArrowDirection = (
  value: number,
): ArrowDirection | undefined => {
  if (value === 0) {
    return undefined;
  }
  return value > 0 ? ArrowDirection.Up : ArrowDirection.Down;
};

/**
 * Whether a formatted delta string rounds to zero (e.g. "$0.00", "-0.0%").
 * The raw delta's direction is derived before rounding, so a tiny non-zero
 * value can display as "0.00" while still carrying a misleading +/- arrow;
 * such cells must render neutral.
 */
export const isFormattedZero = (formatted: string): boolean =>
  parseFloat(formatted.replace(/[^0-9.-]/g, '')) === 0;

/**
 * Resolves a raw delta into the arrow direction and display string the cell
 * should render. When the value rounds to zero at display precision the result
 * is neutral — no arrow and no leading sign — so a tiny non-zero delta never
 * shows as "+$0.00" / "-$0.00".
 */
export const resolveDelta = (
  value: number,
  formatter: (value: number) => string,
): { direction: ArrowDirection | undefined; formattedValue: string } => {
  const formattedValue = formatter(value);
  return isFormattedZero(formattedValue)
    ? { direction: undefined, formattedValue: formattedValue.replace(/^-/, '') }
    : { direction: getArrowDirection(value), formattedValue };
};

export const getDeltaAttributes = (
  direction: ArrowDirection | undefined,
  theme: Theme,
  hasInvertedColor = false,
) => {
  if (!direction) {
    return { color: '', Icon: null };
  }

  const isUp = direction === ArrowDirection.Up;
  const Icon = isUp ? DeltaUpIcon : DeltaDownIcon;

  const color = isUp
    ? hasInvertedColor
      ? theme.sk.color.icon.negative.secondary
      : theme.sk.color.icon.positive.secondary
    : hasInvertedColor
      ? theme.sk.color.icon.positive.secondary
      : theme.sk.color.icon.negative.secondary;

  return { color, Icon };
};

export const variantPackGlobalFilterFn: FilterFn<VariantPackCostChange> = (
  row,
  _columnId,
  filterValue,
) => {
  const searchTerm = (filterValue as string).toLowerCase();
  return [row.original.variantPackName, row.original.variantPackId].some(
    (field) => field.toLowerCase().includes(searchTerm),
  );
};

export const pricingItemGlobalFilterFn: FilterFn<
  PricingItemManagementTableRow
> = (row, _columnId, filterValue) => {
  const searchTerm = (filterValue as string).toLowerCase();
  const { original } = row;
  if (!('restaurantName' in original)) {
    return true;
  }
  return [original.restaurantName, original.restaurantAccountId].some((field) =>
    field.toLowerCase().includes(searchTerm),
  );
};

export interface PricingReviewFilterValue {
  matches?: (row: Row<PricingGroupedTableRow>) => boolean;
  text: string;
}

const matchesSearchText = (
  row: Row<PricingGroupedTableRow>,
  searchTerm: string,
): boolean => {
  if (searchTerm.length === 0) {
    return true;
  }

  const searchFields = [
    row.original.variantPackName,
    row.original.variantPackId,
  ];

  if ('restaurantName' in row.original) {
    searchFields.push(
      row.original.restaurantName,
      row.original.restaurantAccountId,
    );
  }

  return searchFields.some((field) => field.toLowerCase().includes(searchTerm));
};

export const pricingReviewGlobalFilterFn: FilterFn<PricingGroupedTableRow> = (
  row,
  _columnId,
  filterValue,
) => {
  const { matches, text } =
    typeof filterValue === 'string'
      ? { matches: undefined, text: filterValue }
      : (filterValue as PricingReviewFilterValue);

  const searchTerm = text.toLowerCase();

  if (!matches) {
    return matchesSearchText(row, searchTerm);
  }

  // Leaf-up filtering: a parent's own aggregate could pass on its own and
  // skip narrowing to just the children that actually match.
  if (row.depth === 0) {
    return false;
  }

  return matchesSearchText(row, searchTerm) && matches(row);
};

export const IMPACT_COLUMN_CONFIGS: {
  columnId: ColumnId;
  fieldName: keyof VariantPackCostChange | null;
  getImpact: (row: VariantPackCostChange) => number;
}[] = [
  {
    columnId: ColumnId.WeeklyCostChangeGrossProfitImpact,
    fieldName: 'costChangeGrossProfitImpactInMicros',
    getImpact: (row) => row.costChangeGrossProfitImpactInMicros,
  },
  {
    columnId: ColumnId.WeeklyEditsGrossProfitImpact,
    fieldName: 'editsGrossProfitImpactInMicros',
    getImpact: (row) => row.editsGrossProfitImpactInMicros,
  },
  {
    columnId: ColumnId.WeeklyNetGrossProfitImpact,
    fieldName: 'netGrossProfitImpactInMicros',
    getImpact: (row) => row.netGrossProfitImpactInMicros,
  },
];
